import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

export default function AddModal({ open, handleClose, handleSnack, onSuccess, sites }) {
  const [formData, setFormData] = useState({
    name: "",
    ID_Site: "",
    Status: "Active",
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  
  // Reset form when modal is opened
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);
  
  const resetForm = () => {
    setFormData({
      name: "",
      ID_Site: "",
      Status: "Active",
    });
    setErrors({});
    setGeneralError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    
    // Clear error for this field when it changes
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    
    // Validation basée sur les règles du TrajetController.php
    if (!formData.name) {
      newErrors.name = "Le nom est requis";
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  const handleSubmit = async () => {
    // Reset errors
    setErrors({});
    setGeneralError("");
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Send data to API
      const response = await api.post("/trajets", formData);
      
      // Handle success
      handleSnack("Trajet ajouté avec succès", "success");
      onSuccess(); // Refresh the list
      handleClose(); // Close the modal
    } catch (error) {
      console.error("Error adding trajet:", error);
      
      if (error.response) {
        // Handle validation errors (status 400 or 422)
        if (error.response.status === 400 || error.response.status === 422) {
          const serverErrors = error.response.data;
          const newErrors = {};
          
          // Map server validation errors to form fields
          if (serverErrors.name) {
            newErrors.name = Array.isArray(serverErrors.name) 
              ? serverErrors.name[0] 
              : serverErrors.name;
          }
          
          if (serverErrors.ID_Site) {
            newErrors.ID_Site = Array.isArray(serverErrors.ID_Site) 
              ? serverErrors.ID_Site[0] 
              : serverErrors.ID_Site;
          }
          
          setErrors(newErrors);
        } else if (error.response.status === 500) {
          // Server error
          setGeneralError("Une erreur serveur s'est produite. Veuillez réessayer plus tard.");
        } else {
          // Other HTTP errors
          setGeneralError(error.response.data?.message || "Une erreur s'est produite lors de l'ajout du trajet");
        }
      } else {
        // Network error
        setGeneralError("Erreur de connexion au serveur. Veuillez vérifier votre connexion internet.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
        AJOUTER UN TRAJET
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        
        <Typography variant="subtitle2">Site</Typography>
        <FormControl fullWidth sx={inputStyle} error={Boolean(errors.ID_Site)}>
          <Select name="ID_Site" value={formData.ID_Site} onChange={handleChange}>
            <MenuItem value="">Sélectionner un site</MenuItem>
            {sites.map((site) => (
              <MenuItem key={site.ID_Site} value={site.ID_Site}>
                {site.name}
              </MenuItem>
            ))}
          </Select>
          {errors.ID_Site && <FormHelperText error>{errors.ID_Site}</FormHelperText>}
        </FormControl>
        
        <Typography variant="subtitle2">Nom</Typography>
        <TextField 
          fullWidth 
          name="name" 
          value={formData.name} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.name)}
          helperText={errors.name}
        />
        
        <Typography variant="subtitle2">Statut</Typography>
        <RadioGroup row name="Status" value={formData.Status} onChange={handleChange}>
          <FormControlLabel value="Active" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Active" />
          <FormControlLabel value="Inactive" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Inactive" />
        </RadioGroup>
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CloseIcon />} onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleSubmit}
          disabled={loading}
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={<CheckIcon />}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
